import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Service } from 'types/rest/assetCatalogTypes';
import { ApplicationState } from '../../../store/rootReducer';
import Switcher from '../../../components/ui/switcher/Switcher';
import ServicePicker from '../../../components/ui/servicePicker/ServicePicker';
import BzmIcon from '../../../components/icons/BzmIcon';
import styles from './BlueprintApplyModal.module.css';

type Mode = 'new' | 'existing';

interface Props {
  blueprintName: string;
  onApply: (
    target:
      | { newServiceName: string; serviceId?: never; serviceName?: never }
      | { serviceId: number; serviceName: string; newServiceName?: never },
    skipBlazeData: boolean,
  ) => void;
  onCancel: () => void;
}

const BlueprintApplyModal = ({ blueprintName, onApply, onCancel }: Props) => {
  const { t } = useTranslation('blueprints');
  const services = useSelector((s: ApplicationState) => s.services.services);

  const [mode, setMode] = useState<Mode>('new');
  const [serviceName, setServiceName] = useState(blueprintName);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [skipBlazeData, setSkipBlazeData] = useState(false);

  const nameTaken =
    mode === 'new' &&
    serviceName.trim().length > 0 &&
    services.some(s => s.name.toLowerCase() === serviceName.trim().toLowerCase());

  const canApply =
    mode === 'new'
      ? serviceName.trim().length > 0 && !nameTaken
      : selectedService != null && selectedService.id != null;

  const handleApply = () => {
    if (!canApply) return;
    if (mode === 'new') {
      onApply({ newServiceName: serviceName.trim() }, skipBlazeData);
    } else {
      onApply({ serviceId: selectedService!.id as number, serviceName: selectedService!.name }, skipBlazeData);
    }
  };

  return (
    <Modal show onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>{t('apply.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Mode selector — option cards */}
        <div className={styles.cards}>
          <div
            className={`${styles.card} ${mode === 'new' ? styles.cardSelected : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setMode('new')}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') setMode('new');
            }}
          >
            <span className={styles.iconBox}>
              <BzmIcon variant="icon-bzm-add" size={20} />
            </span>
            <div className={styles.cardTitle}>{t('apply.modeNew')}</div>
            <div className={styles.cardSub}>{t('apply.modeNewDesc')}</div>
          </div>
          <div
            className={`${styles.card} ${mode === 'existing' ? styles.cardSelected : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setMode('existing')}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') setMode('existing');
            }}
          >
            <span className={styles.iconBox}>
              <BzmIcon variant="icon-bzm-ct-import" size={20} />
            </span>
            <div className={styles.cardTitle}>{t('apply.modeExisting')}</div>
            <div className={styles.cardSub}>{t('apply.modeExistingDesc')}</div>
          </div>
        </div>

        {mode === 'new' && (
          <>
            <label className="small font-weight-bold">{t('apply.serviceName')}</label>
            <input
              data-test="bp-apply-name"
              className={`form-control mb-1 ${nameTaken ? 'is-invalid' : ''}`}
              value={serviceName}
              onChange={e => setServiceName(e.target.value)}
            />
            {nameTaken && <div className="invalid-feedback d-block mb-2">{t('apply.serviceNameTaken')}</div>}
          </>
        )}

        {mode === 'existing' && (
          <>
            <label className="small font-weight-bold">{t('apply.existingService')}</label>
            <div className="mb-3">
              <ServicePicker
                selectHandler={(svc: Service | null) => setSelectedService(svc)}
                isSelectAll={false}
                isCreateEnabled={false}
                isMaintenanceEnabled={false}
                preSelected={selectedService?.id}
                maxHeight="230px"
              />
            </div>
            <Switcher
              size="md"
              checked={skipBlazeData}
              onChange={() => setSkipBlazeData(v => !v)}
              label={t('apply.ignoreBlazeData')}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-link" onClick={onCancel}>
          {t('apply.cancel')}
        </button>
        <button
          type="button"
          data-test="bp-apply"
          className="btn btn-primary"
          disabled={!canApply}
          onClick={handleApply}
        >
          {t('apply.apply')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BlueprintApplyModal;
